import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AudioStatus, createAudioPlayer } from 'expo-audio';
import type { AudioPlayer as ExpoAudioPlayer } from 'expo-audio';
import ProgressSlider, { ProgressSliderProps } from './ProgressSlider';
import AudioPlayerControls from './AudioPlayerControls';
import Stack from '../../v2BaseComponents/Stack';
import { getSpacing } from '../../../utils/spacing';
import { MedicalProfileFiles } from '../../../primary-record-types';
import { useIsFocused } from '@react-navigation/native';

export type RenderLoadedTranscriptArgs = {
    isLoading: false;
    position: number | null;
    transcript: MedicalProfileFiles.Transcript;
    isPlaying: boolean;
    onSetPosition?: (position: number) => void;
};

export type RenderLoadingTranscriptArgs = {
    isLoading: true;
};

export type RenderTranscriptArgs = RenderLoadedTranscriptArgs | RenderLoadingTranscriptArgs;

export type RenderTranscript = (args: RenderTranscriptArgs) => React.ReactNode;

export type AudioPlayerOwnProps = {
    audioUrl: string;
    startTime?: number;
    endTime?: number;
    renderTranscript?: RenderTranscript;
    transcript?: MedicalProfileFiles.Transcript;
    isLoadingTranscript?: boolean;
    duration: number | null;
};

export type AudioPlayerProps = AudioPlayerOwnProps;

const AudioPlayer: React.FC<AudioPlayerProps> = (props) => {
    const soundRef = useRef<ExpoAudioPlayer | null>(null);
    const forcedDuration = useMemo<number | null>(() => {
        if (props.endTime !== undefined && props.startTime !== undefined) {
            return props.endTime - props.startTime;
        }
        return null;
    }, [props.endTime, props.startTime]);
    const [position, setPosition] = useState<number | null>(null);
    const [seekPosition, setSeekPosition] = useState<number | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasPlayed, setHasPlayed] = useState(false);
    const isFocused = useIsFocused();

    useEffect(() => {
        if (isPlaying) {
            setHasPlayed(true);
        }
    }, [isPlaying, hasPlayed]);
    const onPlaybackStatusUpdate = (status: AudioStatus) => {
        if (!soundRef.current?.isLoaded) return;

        const positionMillis = Math.round(status.currentTime * 1000);
        setPosition(positionMillis);

        if (props.endTime && positionMillis >= props.endTime) {
            soundRef.current.pause();
        }

        if (status.didJustFinish) {
            setIsPlaying(false);
        }
    };
    const onPlaybackStatusUpdateRef = useRef(onPlaybackStatusUpdate);
    onPlaybackStatusUpdateRef.current = onPlaybackStatusUpdate;

    const initSoundAndPlay = async (position: number) => {
        const sound = createAudioPlayer({ uri: props.audioUrl });
        sound.addListener('playbackStatusUpdate', (status) => onPlaybackStatusUpdateRef.current(status));
        await sound.seekTo(position / 1000);
        sound.play();
        soundRef.current = sound;
    };

    const playSoundFromPosition = async (position: number) => {
        if (soundRef.current) {
            await soundRef.current.seekTo(position / 1000);
            soundRef.current.play();
            setIsPlaying(true);
        } else {
            initSoundAndPlay(position).then();
        }
    };

    const onPositionChange = (value: number) => {
        let validatedValue = value;
        if (value < 0) {
            validatedValue = 0;
        } else if (props.duration !== null && value > props.duration) {
            validatedValue = props.duration;
        }

        if (isPlaying) {
            playSoundFromPosition(validatedValue).then();
        }
        setPosition(validatedValue);
    };

    const pauseSound = () => {
        soundRef.current?.pause();
        setIsPlaying(false);
    };

    async function playSound() {
        setIsPlaying(true);
        if (soundRef.current) {
            playSoundFromPosition(position || 0).then();
        } else {
            await initSoundAndPlay(props.startTime || position || 0);
        }
    }

    useEffect(() => {
        return () => {
            soundRef.current?.remove();
        };
    }, []);

    useEffect(() => {
        if (!isFocused) {
            pauseSound();
        }
    }, [isFocused]);

    const getSliderProps = (): ProgressSliderProps => {
        if (forcedDuration) {
            return {
                duration: forcedDuration || 10000,
                isDisabled: props.duration === null,
                currentTime: Math.max((position || 0) - (props.startTime || 0), 0),
                onValueChange: (value) => {
                    onPositionChange(value + (props.startTime || 0));
                },
                onSlideStart: pauseSound,
                onSeekPositionChange: setSeekPosition,
                seekPosition: seekPosition,
            };
        } else {
            return {
                duration: props.duration || 10000,
                currentTime: position || 0,
                onValueChange: onPositionChange,
                onSlideStart: pauseSound,
                onSeekPositionChange: setSeekPosition,
                seekPosition: seekPosition,
                isDisabled: props.duration === null,
            };
        }
    };

    return (
        <>
            <Stack vertical space={2} align={'center'} fullWidth>
                <Stack
                    horizontal
                    fullWidth
                    justify={'center'}
                    style={{
                        paddingLeft: getSpacing(8),
                        paddingRight: getSpacing(8),
                    }}
                >
                    <Stack
                        horizontal
                        fullWidth
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <ProgressSlider {...getSliderProps()} />
                    </Stack>
                </Stack>
                <AudioPlayerControls
                    onPlay={playSound}
                    onPause={pauseSound}
                    isPlaying={isPlaying}
                    position={seekPosition === null ? position || 0 : seekPosition}
                    duration={props.duration}
                />
            </Stack>
            {props.transcript || props.isLoadingTranscript
                ? props.renderTranscript?.(
                      props.isLoadingTranscript
                          ? ({ isLoading: true } as RenderLoadingTranscriptArgs)
                          : ({
                                isLoading: false,
                                position,
                                transcript: props.transcript,
                                isPlaying,
                                onSetPosition: (position) => {
                                    onPositionChange(position);
                                },
                            } as RenderLoadedTranscriptArgs),
                  ) || null
                : null}
        </>
    );
};

export default React.memo(AudioPlayer);
